import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'
import { createSelector } from '@reduxjs/toolkit'

import type { ReduxState } from 'src/store/types'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { getAllGamesProductsSelector } from 'store/modules/games/selectors'
import { getGoldenKeySelector } from 'store/modules/user/selectors'

import { isInfiniteGame } from '../games/utils'

const PAUSE_PERIOD = 5
const MINUTES = 60 * 1000

const getSlotGameFlow = (state: ReduxState) => state.slotGameFlow

const getLowOnCoinsLastDateOpened = (state: ReduxState) => state.slotGameFlow.lowOnCoinsLastDateOpened

export const getGoldMinBet = (state: ReduxState) => state.slotGameFlow.goldMinBet || 50
export const getSweepstakeMinBet = (state: ReduxState) => state.slotGameFlow.sweepstakeMinBet || 0.25

export const getActiveGameCodeSelector = createSelector(getSlotGameFlow, (slotGameFlow) => slotGameFlow.activeGameCode)
export const getGamesWithFreeSpinsSelector = createSelector(getSlotGameFlow, (slotGameFlow) => slotGameFlow.freeSpins)

export const getActiveSlotSelector = createSelector(
  getAllGamesProductsSelector,
  getActiveGameCodeSelector,
  (games, gameCode) => {
    return games?.find((game) => game.code === gameCode)
  },
)

export const isGameOnlyForGoldModeSelector = createSelector(
  getActiveCurrencyIdSelector,
  getGoldenKeySelector,
  (_: ReduxState, currentGame: GameProduct) => currentGame,
  (currency, goldenKey, game) => {
    if (isInfiniteGame(game.tags)) {
      return false
    }

    if (game?.type === 'bingo') {
      return false
    }

    if (game?.mode === 'gold' && currency === 'SC') {
      return true
    }

    return game?.mode === 'gold' && !goldenKey
  },
)

export const isGameForInfiniteModeSelector = createSelector(
  getActiveCurrencyIdSelector,
  (_: ReduxState, currentGame: GameProduct) => isInfiniteGame(currentGame.tags),
  (currency, infiniteGame) => {
    return currency === 'SC' && infiniteGame
  },
)

export const getMinBetSelector = createSelector(
  getActiveCurrencyIdSelector,
  getGoldMinBet,
  getSweepstakeMinBet,
  (activeCurrency, goldMinBet, sweepstakeMinBet) => {
    return activeCurrency === 'GC' ? goldMinBet : sweepstakeMinBet
  },
)

export const getLowOnCoinsDialogDelaySelector = createSelector(
  getLowOnCoinsLastDateOpened,
  (_: ReduxState, currentTime: number) => currentTime,
  (lastDateOpened, currentTime) => {
    if (lastDateOpened) {
      const differenceInTime = currentTime - new Date(lastDateOpened).getTime()
      const differenceInMinutes = differenceInTime / MINUTES

      return differenceInMinutes >= PAUSE_PERIOD
    }

    return true
  },
)

export const freeSpinsLoadedSelector = createSelector(getSlotGameFlow, (slotGameFlow) => slotGameFlow.freeSpinsLoaded)
